import React, { useCallback, useEffect } from "react"
import { X, HelpCircle, AlertCircle, Shuffle } from "lucide-react"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
// @ts-ignore
import Toggle from "react-toggle"
import "react-toggle/style.css"
import { Tooltip } from "./ui/Tooltip"
import { CoverageSelect } from "./CoverageSelect"
import { CourierSelect } from "./CourierSelect"
import { WorkingTimeInput } from "./WorkingTimeInput"

interface PolygonFormProps {
  polygon: any
  onSubmit: (data: any) => void
  onClose: () => void
  isAdminView?: boolean
  selectedCommune?: string | null
  isFullscreen?: boolean
}

/**
 * Validates that a string is a valid GeoJSON coordinate ring:
 * a JSON array of [lng, lat] pairs with at least 3 distinct points.
 */
const validateCoordinatesText = (value: string | undefined): true | string => {
  if (!value || value.trim() === "") return true // optional field; blank means "keep original"
  let parsed: any
  try {
    parsed = JSON.parse(value)
  } catch {
    return "El formato no es JSON válido. Use [[lng, lat], [lng, lat], ...]"
  }
  if (!Array.isArray(parsed)) return "Debe ser un array JSON: [[lng, lat], ...]"
  if (parsed.length < 3) return "Se necesitan al menos 3 puntos para formar un polígono"
  for (let i = 0; i < parsed.length; i++) {
    const pair = parsed[i]
    if (!Array.isArray(pair) || pair.length < 2) return `Punto ${i + 1}: debe ser un par [lng, lat]`
    const [lng, lat] = pair
    if (typeof lng !== "number" || typeof lat !== "number")
      return `Punto ${i + 1}: lng y lat deben ser números`
    if (lng < -180 || lng > 180) return `Punto ${i + 1}: longitud (${lng}) fuera de rango [-180, 180]`
    if (lat < -90 || lat > 90) return `Punto ${i + 1}: latitud (${lat}) fuera de rango [-90, 90]`
  }
  return true
}

const createSchema = (isAdminView: boolean) => {
  const baseSchema = {
    id: yup
      .string()
      .required("El ID es requerido")
      .matches(/^[a-zA-Z0-9-_]+$/, "El ID solo puede contener letras, números, guiones y guiones bajos"),
    type: yup.string().oneOf(["include", "exclude"], "Tipo inválido").required("El tipo es requerido"),
    coordinatesText: yup
      .string()
      .test("valid-coordinates", "", function (value) {
        const result = validateCoordinatesText(value)
        if (result === true) return true
        return this.createError({ message: result })
      }),
  }

  if (isAdminView) {
    return yup.object({
      ...baseSchema,
      selectedRegions: yup
        .array()
        .test(
          "at-least-one-region",
          "Seleccione al menos una cobertura o un courier",
          function (value) {
            const { courierIds } = this.parent
            const hasRegions = value && value.length > 0
            const hasCouriers = courierIds && courierIds.length > 0
            return hasRegions || hasCouriers
          }
        ),
      status: yup.boolean().required("El estado es requerido"),
      courierIds: yup
        .array()
        .of(yup.string())
        .test(
          "at-least-one-courier",
          "Seleccione al menos un courier o una cobertura",
          function (value) {
            const { selectedRegions } = this.parent
            const hasCouriers = value && value.length > 0
            const hasRegions = selectedRegions && selectedRegions.length > 0
            return hasRegions || hasCouriers
          }
        ),
      workingTime: yup.array(),
    })
  }

  return yup.object(baseSchema)
}

export const PolygonForm: React.FC<PolygonFormProps> = ({
  polygon,
  onSubmit,
  onClose,
  isAdminView = true, // Default to true for this screen
  selectedCommune,
  isFullscreen = false,
}) => {
  console.log("PolygonForm rendering. Polygon data:", polygon)
  console.log("Selected Commune:", selectedCommune)

  const isEditing = Boolean(polygon.id && !polygon.id.startsWith("COMMUNE_")) // If it has a real ID (refining logic)

  // Custom unique ID generator
  const generateUniqueId = () => Math.random().toString(36).substring(2) + Date.now().toString(36)

  const schema = createSchema(isAdminView)

  // Serialize existing coordinates to a text string for the textarea
  const serializeCoordinates = (poly: any): string => {
    try {
      const ring = poly?.polygon?.geometry?.coordinates?.[0]
      if (Array.isArray(ring) && ring.length > 0) {
        return JSON.stringify(ring)
      }
    } catch {
      // ignore
    }
    return ""
  }

  // Prepare default values
  const defaultValues = {
    id: polygon.id || generateUniqueId(),
    type: polygon.type || "include",
    selectedRegions: polygon.coverageIds || [], // Map coverageIds to selectedRegions
    courierIds: polygon.courierIds || [],
    workingTime: polygon.workingTime || [],
    status: typeof polygon.status === "boolean" ? polygon.status : true,
    coordinatesText: serializeCoordinates(polygon),
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema) as any,
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygon])

  const selectedRegions = watch("selectedRegions")
  const status = watch("status")

  const generateRandomId = useCallback(() => {
    const randomId = generateUniqueId()
    setValue("id", randomId, { shouldValidate: true })
  }, [setValue])

  const handleFormSubmit = (data: any) => {
    onSubmit(data)
  }

  return (
    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full max-h-full flex flex-col relative border border-gray-100 dark:border-gray-700 animate-in fade-in zoom-in duration-200">
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/30">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
            {isEditing ? "Editar Polígono" : "Crear Polígono"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* ID Field */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  ID del polígono
                </label>
                <div className="relative">
                  <Tooltip content="ID único del polígono">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  {...register("id")}
                  disabled={isEditing}
                  className={`flex-1 px-3 py-1.5 text-sm border rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-all
                                        ${errors.id ? "border-red-300 focus:ring-red-500 dark:border-red-500" : "border-gray-300 focus:ring-blue-500 dark:border-gray-500"}
                                        ${isEditing ? "opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-800" : ""}
                                    `}
                  placeholder="Ingrese el ID"
                />
                <button
                  type="button"
                  onClick={generateRandomId}
                  disabled={isEditing}
                  className={`px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg border border-indigo-200 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800 transition-colors
                                        ${isEditing ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}
                                    `}
                  title="Generar ID aleatorio"
                >
                  <Shuffle className="w-4 h-4" />
                </button>
              </div>
              {errors.id && (
                <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.id.message as string}
                </p>
              )}
            </div>

            {/* Grid for Type and Status */}
            <div className="grid grid-cols-2 gap-4">
              {/* Type Field */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Tipo
                </label>
                <select
                  {...register("type")}
                  className={`w-full px-3 py-1.5 text-xs border rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-all ${
                    errors.type
                      ? "border-red-300 focus:ring-red-500 dark:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 dark:border-gray-500"
                  }`}
                >
                  <option value="include">Inclusión</option>
                  <option value="exclude">Exclusión</option>
                </select>
                {errors.type && (
                  <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.type.message as string}
                  </p>
                )}
              </div>

              {/* Status Field */}
              {isAdminView && (
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                    Estado
                  </label>
                  <div className="flex items-center h-[34px]">
                    <Toggle
                      checked={status}
                      onChange={(e: any) => setValue("status", e.target.checked)}
                      icons={false}
                      className="custom-toggle-compact"
                    />
                    <span
                      className={`ml-2 text-xs font-medium ${status ? "text-green-600 dark:text-green-400" : "text-gray-500"}`}
                    >
                      {status ? "ACTIVO" : "INACTIVO"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Admin-only fields */}
            {isAdminView && (
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Coberturas
                  {selectedCommune && (
                    <span className="text-[10px] font-normal text-gray-400 ml-2 normal-case">({selectedCommune})</span>
                  )}
                </label>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-1 bg-gray-50 dark:bg-gray-900/50">
                  <CoverageSelect
                    value={selectedRegions || []}
                    onChange={(value) => setValue("selectedRegions", value, { shouldValidate: true })}
                    selectedCommune={selectedCommune || undefined}
                    isFullscreen={isFullscreen}
                  />
                </div>

                {/* Hidden input to register value handled by CoverageSelect */}
                <input type="hidden" {...register("selectedRegions")} />
                {errors.selectedRegions && (
                  <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.selectedRegions.message as string}
                  </p>
                )}
              </div>
            )}

            {/* Couriers Field */}
            {isAdminView && (
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Couriers
                </label>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-1 bg-gray-50 dark:bg-gray-900/50">
                  <CourierSelect
                    value={watch("courierIds") || []}
                    onChange={(value) => setValue("courierIds", value)}
                    isFullscreen={isFullscreen}
                  />
                </div>
                {errors.courierIds && (
                  <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.courierIds.message as string}
                  </p>
                )}
              </div>
            )}

            {/* Working Time Field */}
            {isAdminView && (
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Horario de Trabajo
                </label>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/50">
                  <WorkingTimeInput
                    value={watch("workingTime") || []}
                    onChange={(value) => setValue("workingTime", value)}
                  />
                </div>
              </div>
            )}

            {/* Coordinates Field */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Coordenadas
                </label>
                <Tooltip content="Array JSON con pares [longitud, latitud]. Ejemplo: [[-70.6, -33.4], [-70.5, -33.4], ...]">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                  </button>
                </Tooltip>
              </div>
              <textarea
                {...register("coordinatesText")}
                rows={4}
                spellCheck={false}
                className={`w-full px-3 py-2 text-xs font-mono border rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-all resize-y ${
                  errors.coordinatesText
                    ? "border-red-300 focus:ring-red-500 dark:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 dark:border-gray-500"
                }`}
                placeholder='[[lng, lat], [lng, lat], ...]'
              />
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Formato: <code>[[lng, lat], [lng, lat], ...]</code> — Déjalo en blanco para conservar los vértices actuales.
              </p>
              {errors.coordinatesText && (
                <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.coordinatesText.message as string}
                </p>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 flex justify-end gap-3 p-4 bg-gray-50/50 dark:bg-gray-700/30 border-t dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded-lg shadow-sm hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isEditing ? "Guardar cambios" : "Crear"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
